import { use, useEffect } from 'react'

import { motion } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { Box, useDisclosure } from '@chakra-ui/react'

import { HeaderContext } from '@/context/Header'
import { isAllowlistedForPublicApi } from '@/helpers/is-allowlisted'
import { useBillingInfo, useIsAdmin, useTeam } from '@/react-query/team'
import { useAllSentTeamInvites } from '@/react-query/team-invites'
import { useTeamMembers } from '@/react-query/team-members'
import { useUser } from '@/react-query/user'
import { Billing } from '@/views/screens/Team/Billing'
import { JoinTeamModal } from '@/views/screens/Team/components/JoinTeamModal'
import { CreateTeam } from '@/views/screens/Team/CreateTeam/CreateTeam'
import { ReceivedTeamInvites } from '@/views/screens/Team/ReceivedTeamInvites'
import { TeamMembers } from '@/views/screens/Team/TeamMembers'
import { TeamName } from '@/views/screens/Team/TeamName'

import { Header } from './components/Header'
import { ApiSettings } from './ApiSettings'
import { TeamSettings } from './TeamSettings'

const Team = () => {
  const { setMiddleContent } = use(HeaderContext)

  const { onOpen, isOpen, onClose } = useDisclosure()

  const { inviteId } = useParams()
  const { data: team, isLoading } = useTeam()

  const isAdmin = useIsAdmin()

  useEffect(() => {
    if (inviteId) {
      onOpen()
    }
  }, [inviteId])

  useEffect(() => {
    setMiddleContent(<Header />)
    // Reset when unmounting
    return () => setMiddleContent(null)
  }, [])

  if (isLoading) {
    return null
  }

  return (
    <Box>
      {team && (
        <Box as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {isAdmin ? <TeamAdminView /> : <TeamMemberView />}
        </Box>
      )}
      {!team && (
        <Box as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <CreateTeam />
        </Box>
      )}
      <ReceivedTeamInvites />
      <JoinTeamModal
        teamInviteId={inviteId}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  )
}

const TeamAdminView = () => {
  const team = useTeam()
  const { data: user } = useUser()

  const billingInfo = useBillingInfo()
  const teamMembers = useTeamMembers()
  const teamInvites = useAllSentTeamInvites()

  const teamExtraDataIsLoading =
    teamMembers.isLoading || teamInvites.isLoading || billingInfo.isLoading

  if (team.isLoading || teamExtraDataIsLoading) {
    return null
  }

  return (
    <>
      <TeamName />
      <Billing />
      <TeamSettings />
      <TeamMembers />
      {isAllowlistedForPublicApi(user?.email) && <ApiSettings />}
    </>
  )
}

const TeamMemberView = () => {
  const team = useTeam()

  const teamMembers = useTeamMembers()

  const teamExtraDataIsLoading = teamMembers.isLoading

  if (team.isLoading || teamExtraDataIsLoading) {
    return null
  }

  return (
    <>
      <TeamName />
      <TeamMembers />
    </>
  )
}

export default Team
